// Web Push client — handles subscription + syncing favorites codes to backend.
import axios from "axios";
import { loadFavorites } from "../components/Favorites";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || "";

function urlB64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const b64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    !!VAPID_PUBLIC_KEY
  );
}

async function getRegistration() {
  if (!("serviceWorker" in navigator)) return null;
  const existing = await navigator.serviceWorker.getRegistration("/sw.js");
  if (existing) return existing;
  // Fallback: try to register directly (App.js does it too but may have failed silently)
  try {
    return await navigator.serviceWorker.register("/sw.js");
  } catch {
    return null;
  }
}

function withTimeout(promise, ms, label) {
  return Promise.race([
    promise,
    new Promise((_, rej) =>
      setTimeout(() => rej(new Error(`Timeout (${ms / 1000}s) su ${label}`)), ms)
    ),
  ]);
}

/** Subscribe to Web Push and register with the backend. Returns the PushSubscription. */
export async function subscribeToPush(quietHours = false) {
  if (!pushSupported()) throw new Error("Push non supportato da questo browser");
  // Some Android devices with sluggish Google Play Services take several
  // seconds to hand back a ServiceWorkerRegistration — give it 10s.
  let reg;
  try {
    reg = await withTimeout(getRegistration(), 10000, "registrazione service worker");
  } catch (e) {
    throw new Error(
      "Impossibile registrare il Service Worker. Prova a ricaricare l'app, controlla la connessione e verifica che Google Play Services sia aggiornato."
    );
  }
  if (!reg) throw new Error("Service worker non registrato");

  // Reading an existing subscription can hang on Android when FCM is unreachable.
  // Treat any error/timeout here as "no existing subscription" and proceed —
  // pushManager.subscribe() below has its own 15s timeout with a helpful message.
  let sub = null;
  try {
    sub = await withTimeout(
      reg.pushManager.getSubscription(),
      6000,
      "lettura sottoscrizione esistente"
    );
  } catch { /* fall through: sub stays null */ }
  // If an existing subscription uses a different applicationServerKey (e.g. from
  // a previous app version), unsubscribe first so the new key is applied.
  if (sub) {
    try {
      const existingKey = sub.options?.applicationServerKey;
      const wantKey = urlB64ToUint8Array(VAPID_PUBLIC_KEY);
      const same = existingKey && new Uint8Array(existingKey).toString() === wantKey.toString();
      if (!same) {
        await sub.unsubscribe();
        sub = null;
      }
    } catch { /* ignore */ }
  }
  if (!sub) {
    try {
      sub = await withTimeout(
        reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlB64ToUint8Array(VAPID_PUBLIC_KEY),
        }),
        15000,
        "pushManager.subscribe (Google Play Services / FCM)"
      );
    } catch (e) {
      const msg = e.message || e.name || String(e);
      if (/timeout/i.test(msg)) {
        throw new Error(
          "Il servizio Google Play Services non risponde. Verifica connessione, VPN e che Google Play Services sia aggiornato."
        );
      }
      throw new Error(`pushManager.subscribe fallito: ${msg}`);
    }
  }
  const favs = loadFavorites();
  const codes = favs.map((f) => f.code).filter(Boolean);
  try {
    await axios.post(`${API}/push/subscribe`, {
      subscription: sub.toJSON(),
      favorites: codes,
      quiet_hours: !!quietHours,
    }, { timeout: 8000 });
  } catch (e) {
    throw new Error(`Registrazione al server fallita: ${e.message}`);
  }
  return sub;
}

/** Push an updated favorites/quiet-hours snapshot to the backend if we already have a subscription. */
export async function syncPushFavorites(quietHours = null) {
  if (!pushSupported()) return;
  const reg = await getRegistration();
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const favs = loadFavorites();
  const codes = favs.map((f) => f.code).filter(Boolean);
  const body = { endpoint: sub.endpoint, favorites: codes };
  if (quietHours !== null) body.quiet_hours = !!quietHours;
  try {
    await axios.post(`${API}/push/update`, body, { timeout: 10000 });
  } catch { /* silent */ }
}

/** Remove push subscription (both browser and backend). */
export async function unsubscribeFromPush() {
  if (!pushSupported()) return;
  const reg = await getRegistration();
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  try { await sub.unsubscribe(); } catch { /* ignore */ }
  try {
    await axios.post(`${API}/push/unsubscribe`, { endpoint }, { timeout: 10000 });
  } catch { /* ignore */ }
}

/** Send a test notification via the backend to the current subscription. */
export async function sendTestPush() {
  if (!pushSupported()) throw new Error("push-unsupported");
  const reg = await getRegistration();
  if (!reg) throw new Error("no-service-worker");
  const sub = await reg.pushManager.getSubscription();
  if (!sub) throw new Error("no-subscription");
  const r = await axios.post(`${API}/push/test`, { endpoint: sub.endpoint }, { timeout: 15000 });
  if (!r.data?.ok) {
    const msg = r.data?.detail || r.data?.error || "invio fallito";
    const err = new Error(msg);
    err.reason = r.data?.error;
    throw err;
  }
}
